import type { Schedule } from "../../types";
import { formatTime } from "../../lib/dateUtils";
import { useEdition } from "../../hooks/useEdition";
import { useTranslations } from "../../hooks/useTranslations";
import { EmptyState } from "../ui/EmptyState";
import { ScheduleItem } from "./ScheduleItem";

type ScheduleByTimeProps = {
  schedules: Schedule[];
  onArtistClick: (artistId: string, elementId?: string) => void;
  onEventClick: (eventId: string, elementId?: string) => void;
};

export function ScheduleByTime({
  schedules,
  onArtistClick,
  onEventClick,
}: ScheduleByTimeProps) {
  const { timeFormat } = useEdition();
  const t = useTranslations();
  // Group schedules by start time (hour)
  const timeMap = new Map<string, Schedule[]>();

  for (const schedule of schedules) {
    // Always group by 24h hour key for consistent ordering
    const timeKey = schedule.start_time
      ? schedule.start_time.slice(0, 2) + ":00"
      : "No time";

    const existing = timeMap.get(timeKey);
    if (existing) {
      existing.push(schedule);
    } else {
      timeMap.set(timeKey, [schedule]);
    }
  }

  // Sort time slots: regular times ascending, then next-day times (00:00-dayStart) ascending, then "No time"
  const timeSlots = Array.from(timeMap.entries());

  // Sort schedules within each time slot
  for (const [, slotSchedules] of timeSlots) {
    slotSchedules.sort((a, b) => {
      return `${a.start_date}T${a.start_time ?? ""}`.localeCompare(
        `${b.start_date}T${b.start_time ?? ""}`,
      );    
    });
  }

  if (timeSlots.length === 0) {
    return <EmptyState message={t.noSchedulesForDay} />;
  }

  return (
    <div className="festapp-schedule-by-time">
      {timeSlots.map(([time, slotSchedules]) => (
        <div key={time} className="festapp-schedule-time-slot">
          <h3 className="festapp-schedule-time-header">
            {time === "No time" ? time : formatTime(time + ":00", timeFormat)}
          </h3>
          <div className="festapp-schedule-time-items">
            {slotSchedules.map((schedule) => (
              <ScheduleItem
                key={schedule._id}
                schedule={schedule}
                itemId={schedule._id}
                onArtistClick={(artistId) => onArtistClick(artistId, schedule._id)}
                onEventClick={(eventId) => onEventClick(eventId, schedule._id)}
                showPlace
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
